// src/store/slices/workTaskSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import workTaskApi from 'api/workTaskApi';

const initialState = {
  tasks: [],
  selectedTask: null,
  pagination: { count: 0, page: 1, page_size: 10 },
  isLoading: false,
  error: null,
};

// Fetch paginated tasks
export const fetchTasks = createAsyncThunk(
  'workTask/fetchTasks',
  async (params, { rejectWithValue }) => {
    try {
      const res = await workTaskApi.getTasks(params);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch tasks');
    }
  }
);

// Fetch single task
export const fetchTaskById = createAsyncThunk(
  'workTask/fetchTaskById',
  async (id, { rejectWithValue }) => {
    try {
      const res = await workTaskApi.getTaskById(id);
      return res.data || res;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load task details');
    }
  }
);

// Create task
export const createTask = createAsyncThunk(
  'workTask/createTask',
  async (data, { rejectWithValue }) => {
    try {
      const res = await workTaskApi.createTask(data);
      return res.data || res;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create task');
    }
  }
);

// Update task
export const updateTask = createAsyncThunk(
  'workTask/updateTask',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await workTaskApi.updateTask(id, formData);
      return res.data || res;
    } catch (err) {
      console.log("eee", err)
      return rejectWithValue(err.response?.data?.message || 'Failed to update task');
    }
  }
);

// Delete task
export const deleteTask = createAsyncThunk(
  'workTask/deleteTask',
  async (id, { rejectWithValue }) => {
    try {
      await workTaskApi.deleteTask(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete task');
    }
  }
);

// Import tasks
export const importTasksAction = createAsyncThunk(
  'workTask/importTasks',
  async (file, { rejectWithValue }) => {
    try {
      const res = await workTaskApi.importTasks(file);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Import failed');
    }
  }
);

// Export tasks (triggers file download)
export const exportTasksAction = createAsyncThunk(
  'workTask/exportTasks',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await workTaskApi.exportTasks();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'work-tasks-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Export failed');
    }
  }
);

const workTaskSlice = createSlice({
  name: 'workTask',
  initialState,
  reducers: {
    clearWorkTaskError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch list
      .addCase(fetchTasks.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasks = action.payload.data || [];
        state.pagination = action.payload.pagination || { count: action.payload.data?.length || 0, page: 1, page_size: 10 };
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch single
      .addCase(fetchTaskById.pending, (state) => { state.isLoading = true; })
      .addCase(fetchTaskById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedTask = action.payload;
      })
      .addCase(fetchTaskById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createTask.pending, (state) => { state.isLoading = true; })
      .addCase(createTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasks.unshift(action.payload);
        state.pagination.count += 1;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateTask.pending, (state) => { state.isLoading = true; })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.tasks.findIndex(t => t.id === action.payload.id);
        if (index !== -1) state.tasks[index] = action.payload;
        state.selectedTask = action.payload;
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteTask.pending, (state) => { state.isLoading = true; })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasks = state.tasks.filter(t => t.id !== action.payload);
        state.pagination.count = Math.max(0, state.pagination.count - 1);
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Import / Export (minimal state change)
      .addCase(importTasksAction.pending, (state) => { state.isLoading = true; })
      .addCase(importTasksAction.fulfilled, (state) => { state.isLoading = false; })
      .addCase(importTasksAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(exportTasksAction.pending, (state) => { state.isLoading = true; })
      .addCase(exportTasksAction.fulfilled, (state) => { state.isLoading = false; })
      .addCase(exportTasksAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearWorkTaskError } = workTaskSlice.actions;
export default workTaskSlice.reducer;